import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import Vue from "vue";
import { RootState } from "../store";
import { OnBoardingPage } from "../../../../utils/models/OnBoardingPage";
import { OnBoardingGoal } from "../../../../utils/models/OnBoardingGoal";
import graphClient from "../../../../utils/graphql/graph_client";

import { SignInProvider } from "../../../../utils/models/SignInProvider";
import { auth, createUserWithEmailAndPassword } from '../../../../utils/firebase/auth';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth'
import { friendlyAuthError } from "../../../../utils/firebase/auth_errors";
import { Modal } from "bootstrap";

import { FacebookAuthProvider, GoogleAuthProvider, User, OAuthProvider } from 'firebase/auth';

import {
  CreateHikeList,
  GetUser,
  GetUserHikeLists,
  AddHikeToList,
  RemoveHikeFromList,
  UpdateUser
} from "../../../../utils/graphql/user-graph";
import { GetCountries } from "../../../../utils/graphql/country-graph";
import { GenerateDirectUpload } from "../../../../utils/graphql/user-graph";
import { absoluteAvatarUrl } from "../../../../utils/helpers/presetAvatars";
import { parseLocales } from "../../../../i18n/supportedLocales";
import SparkMD5 from "spark-md5";
import { hexDigestToBase64 } from "../../../../utils/hexDigestToBase64";
import {
  composeFullName,
  dedupeCountries,
  initialFullName,
  nameSeed,
  isDeliberateSignupGoal,
  profileSetupMayApply,
  shouldRunProfileSetup,
  splitFullName,
  suggestUsernames,
  parsePreferenceOptions,
  settingsPayload,
  isAttributionSource,
  EMPTY_PREFERENCE_OPTIONS,
  type SignupCountry,
  type PreferenceOptions,
} from "../../../../utils/helpers/signupProfile";
import axios_client from "../../../../utils/helpers/axios_client";
import { CopyCuratedTrailToPtv2PlannedTrail } from "../../../../utils/graphql/planned-trail-graph";
import { RevenueCatUtil } from "../../../../utils/models/RevenueCatUser";
import { Offering, RevenueCatOffering } from "../../../../utils/models/RevenueCatOfferings";
import {RevenueCatWrapper} from "../../../../utils/RevenueCatWrapper";
import emitter from "../../../../utils/bus";
import { i18n } from "../../../../i18n";

const t = (key: string): string => i18n.global.t(key);

// The checksum Active Storage's direct upload requires. Thin SparkMD5 glue
// around the tested hexDigestToBase64 helper, matching the `calculateMD5Base64`
// every other uploader in the app keeps locally.
// Mirrors Image::MAX_IMAGE_UPLOAD_BYTES, so an oversized photo is refused here
// rather than after a round trip that ends in a GraphQL error.
const MAX_AVATAR_BYTES = 15 * 1024 * 1024

// Object URLs stay alive until revoked or the document unloads, so each photo
// the user tries would otherwise be held for the rest of the session.
const revokeObjectPreview = (preview?: string | null) => {
  if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview)
}

const md5Base64 = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer()
  const spark = new SparkMD5.ArrayBuffer()
  spark.append(buffer)
  return hexDigestToBase64(spark.end())
}

type OnBoardingTrailContext = {
  hikeId?: string | number
  trailId?: number
  slug?: string
  regionSlug?: string
  countrySlug?: string
}

declare global {
  interface Window {
    landingPageSlug: String;
    isNewUser?: boolean;
    hike_id?: string;
    id?: number;
    slug?: string;
    region_slug?: string;
    country_slug?: string;
    Rewardful?: {
      referral?: string;
    };
  }
}

export enum AuthMethods {
    Email = 'email',
    Google = 'google',
    Apple = 'apple',
    MagicLink = 'magic_link'
}

export class OnBoardingState {
  pageStack: OnBoardingPage[] = [OnBoardingPage.SignUpOptions]
  onBoardingGoal: OnBoardingGoal | null = null
  slug: string = ''
  regionSlug: string = ''
  countrySlug: string = ''
  trailId: number = 0
  hikeId: string = ''
  user: User | null = null
  hiikerUser: any = null
  usersHikeLists: any[] = []
  trail: any = null
  offering: Offering | null = null
  clientSecret: string | null = null
  freeTrialPaymentMethodClientSecret: string | null = null
  paymentMethodClientSecretSubscriptionsUrl: string = window.paymentMethodClientSecretSubscriptionsUrl
  authMethods: AuthMethods[] = [AuthMethods.Google, AuthMethods.Apple, AuthMethods.Email, AuthMethods.MagicLink]
  // UX state — replaces alert()-based error reporting and per-component loading flags
  errorMessage: string | null = null
  // Positive/info confirmations (e.g. "Reset link sent"). Rendered as an
  // alert-success in the components, separate from errorMessage so we don't
  // dress up successes as errors.
  noticeMessage: string | null = null
  isLoading: boolean = false
  // True when the OnBoarding components are mounted on the standalone /login page
  // (not inside the Bootstrap modal). Lets us skip modal.hide()/show() calls that
  // would throw, and redirect on success instead.
  standaloneMode: boolean = false
  // Email captured between the magic-link request screen and the "check your inbox"
  // screen. Also persisted to localStorage so signInWithEmailLink can recover it
  // when the user returns via the link.
  magicLinkEmail: string | null = null
  // True when the user opened the email link in a different browser than the
  // one that requested it (no email in localStorage). The callback page then
  // asks them to re-enter their email so signInWithEmailLink can complete.
  magicLinkNeedsEmail: boolean = false
  // Email address a verification email was just sent to, shown on the post-signup
  // "Check your inbox" screen (OnBoardingPage.VerifyEmailSent).
  verifyEmailAddress: string | null = null
  // --- Profile wizard (Name → Country → Username) --------------------------
  // Mirrors the mobile apps' signup steps. Only populated for a brand-new
  // account on a deliberate signup — see shouldRunProfileSetup.
  countries: SignupCountry[] = []
  countriesLoading: boolean = false
  // True when the country list could not be loaded. The step then offers a
  // retry and lets the user continue without a country, rather than leaving
  // them on a step they can never satisfy.
  countriesFailed: boolean = false
  // Answers collected across the three steps, persisted in one updateUser call
  // at the end so a half-finished wizard never writes a partial profile.
  // First/last are kept apart because that is how the mobile steps ask for them;
  // they are composed into `users.title` on save.
  profileFirstName: string = ''
  profileLastName: string = ''
  profileCountryId: string | number | null = null
  profileUsername: string = ''
  // Suggestions offered on the Username step, derived locally from the name.
  profileUsernameSuggestions: string[] = []
  // --- Photo step ---------------------------------------------------------
  // A preset avatar is sent as a URL the server fetches; an uploaded photo as a
  // direct-upload signed id. Only one is ever set.
  profileAvatarUrl: string | null = null
  profileAvatarKey: string | null = null
  // What the step shows as the current choice — a bundled asset path for a
  // preset, an object URL for a file the user picked.
  profileAvatarPreview: string | null = null
  // Which preset is highlighted. Tracked by id rather than by comparing the
  // bundled asset URL, which is a build-dependent string.
  profileAvatarPresetId: string | null = null
  // A photo the user picked from their device, held until the wizard finishes.
  // Uploading on the step itself would strand a blob whenever someone abandons
  // the signup, so it goes up once, at the end, with everything else.
  profileAvatarFile: File | null = null
  profileAvatarUploading: boolean = false
  // --- Settings step ------------------------------------------------------
  // Option lists come from Rails (window.hiikerPreferenceOptions), never a
  // second copy in JS. Empty means "nothing to offer", and the step lets the
  // user straight through — every one of these has a server-side default.
  preferenceOptions: PreferenceOptions = { ...EMPTY_PREFERENCE_OPTIONS }
  profileMeasurement: string | null = null
  profileGridReference: string | null = null
  profileHikeTime: string | null = null
  profileLocale: string | null = null
  // "How did you hear about HiiKER?" — stored verbatim in users.attribution_tag.
  profileAttributionTag: string | null = null
  // Set once the wizard has written its answers, so re-entering the post-login
  // routing (e.g. via the Check-your-inbox Continue) can't restart it.
  profileSetupCompleted: boolean = false
  // True from the moment an in-page auth attempt starts until it finishes.
  // The standalone /login page watches Firebase auth state so it can bounce a
  // user who arrives already signed in; without this flag that watcher also
  // fires for a sign-in happening *on* the page and races the flow's own
  // routing to navigate away — which is what sent brand-new accounts straight
  // to the dashboard instead of the next signup step.
  authInProgress: boolean = false
}

// Key used to stash the email between requesting and completing the magic link.
// Firebase requires the same email to be passed to signInWithEmailLink as was
// originally passed to sendSignInLinkToEmail.
const MAGIC_LINK_EMAIL_STORAGE_KEY = 'magicLinkEmail'

const actions: ActionTree<OnBoardingState, RootState> = {
  initialiseStore(store) {
    store.commit('setSlugData')

    emitter.on('user:updated', () => {
      store.dispatch('trackMixpanelEvents')
    })
  },
  async setupFlowFor(store, goal: OnBoardingGoal) {
    store.commit('setOnBoardingGoal', goal)
    if (window.user == null) {
      store.commit('setPageStack', [OnBoardingPage.SignUpOptions])
    } else {
      store.commit('setUser', window.user)
      switch (store.state.onBoardingGoal) {
        case OnBoardingGoal.DownloadGPX:
          store.commit('setPageStack', [OnBoardingPage.DownloadGPX])
          break
        case OnBoardingGoal.AddTrailToList:
          store.commit('setPageStack', [OnBoardingPage.AddTrailToList])
          break
        case OnBoardingGoal.CreateAccount:
          store.commit('setPageStack', [OnBoardingPage.SignUpOptions])
          break
        case OnBoardingGoal.Accommodations:
          store.commit('setPageStack', [OnBoardingPage.Success])
          break
        case OnBoardingGoal.TrailMap:
          // If user is already logged in, redirect to the map immediately
          const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
          if (redirectUrl) {
            sessionStorage.removeItem('redirectAfterLogin')
            window.location.href = redirectUrl
            return
          }
          // Fallback to success page if no redirect URL
          store.commit('setPageStack', [OnBoardingPage.Success])
          break
        case OnBoardingGoal.TrailPreview:
          // If user is already logged in, trigger the preview modal
          modal?.hide()
          if (window.showMapModalForTrail && window.trail_id) {
            window.showMapModalForTrail(window.trail_id)
          }
          return
      }
    }
    // Only show the modal when we're actually rendering inside one. The
    // standalone /login page mounts the same components without modal chrome.
    if (!store.state.standaloneMode) {
      const element = document.getElementById('on-boarding-dialog')
      if (element) {
        const modal = new Modal(element)
        modal.show()
      }
    }
  },
  trackMixpanelEvents(store) {
    if (window.user) {
      window.mixpanel.identify(window.user?.uid);
      window.mixpanel.people.set({
        '$email': window.user?.email,  // Use $email for Mixpanel's reserved email property
        'On Boarding Goal': store.state.onBoardingGoal,
      });
      window.mixpanel.track(window.isNewUser ? 'Sign Up Success' : 'Login Success', {
        'On Boarding Goal': store.state.onBoardingGoal,
        'User ID': window.user?.uid,
        'User Email': window.user?.email,
      });
    }
  },
  // Post-auth entry point, called by every sign-in path (email, SSO, magic link,
  // and the Continue on the "Check your inbox" screen).
  //
  // Its only job is to decide whether a brand-new account should walk the
  // mobile-style profile wizard (Name → Country → Username) before being routed
  // on. All the actual routing still lives in routeAfterLogin, which the wizard
  // dispatches when it finishes — so every existing destination behaves exactly
  // as it did, and an interrupted flow (like / add-to-list / voucher) goes
  // straight there without the extra steps.
  // `confirmDetailsFallback` is set by the sign-in paths that have no name from
  // the identity provider (magic link always, SSO sometimes). Those used to go
  // straight to ConfirmDetails; now the wizard gets first refusal, because it
  // asks for the same name plus the country and username. ConfirmDetails stays
  // as the fallback for when the wizard does not apply, so an interruption flow
  // still collects a name exactly as it does today — and the two never stack.
  async setNextPostLoginPage(store, options: { confirmDetailsFallback?: boolean } = {}) {
    // Cheap, network-free check first. Existing users and every interruption
    // flow bail out here without paying for the GetUser below.
    if (!store.state.profileSetupCompleted && profileSetupMayApply({
      isNewUser: window.isNewUser,
      goal: store.state.onBoardingGoal,
      standaloneMode: store.state.standaloneMode
    })) {
      // Fetched to prefill the steps (the name an SSO provider gave us, a
      // country already on the record), not to decide whether to run them —
      // see shouldRunProfileSetup. Best-effort: a failed lookup just means the
      // steps start empty.
      if (!store.state.hiikerUser) {
        try {
          await store.dispatch('getHiikerUser')
        } catch (e) {
          console.warn('[OnBoarding] Could not read the Hiiker user; skipping profile setup.', e)
        }
      }
      if (shouldRunProfileSetup({
        isNewUser: window.isNewUser,
        goal: store.state.onBoardingGoal,
        standaloneMode: store.state.standaloneMode,
        serverUser: store.state.hiikerUser
      })) {
        store.dispatch('startProfileSetup')
        return
      }
    }
    if (options?.confirmDetailsFallback) {
      store.commit('pushPage', OnBoardingPage.ConfirmDetails)
      return
    }
    await store.dispatch('routeAfterLogin')
  },
  async routeAfterLogin(store) {
    // Standalone /login page: just redirect somewhere sensible. There's no
    // modal to hide, no in-page success state to push.
    if (store.state.standaloneMode) {
      const stored = sessionStorage.getItem('redirectAfterLogin')
      if (stored) {
        sessionStorage.removeItem('redirectAfterLogin')
        window.location.href = stored
      } else {
        window.location.href = '/'
      }
      return
    }
    const element = document.getElementById('on-boarding-dialog')
    const modal = element ? Modal.getInstance(element) : null
    switch (store.state.onBoardingGoal) {
      case OnBoardingGoal.DownloadGPX:
        store.commit('pushPage', OnBoardingPage.DownloadGPX)
        break
      case OnBoardingGoal.AddTrailToList:
        store.commit('pushPage', OnBoardingPage.AddTrailToList)
        return
      case OnBoardingGoal.CreateAccount:
        store.commit('pushPage', OnBoardingPage.Success)
        break
      case OnBoardingGoal.MeasurementTool:
        try {
          const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus()
          if (isProOrProPlus) {
            store.commit('pushPage', OnBoardingPage.Success)
          } else {
            window.location.href = "/pricing"
          }
        } catch (e) {

        }
        break
      case OnBoardingGoal.DownloadApp:
        modal?.hide()
        window.openDownloadModal();
        break
      case OnBoardingGoal.RedeemVoucher:
        modal?.hide()
        break
      case OnBoardingGoal.CreateAccountForPurchase:
        // Existing subscribers go on to checkout like anyone else — offers are
        // open to current and past members, so there's no PRO check here.
        modal?.hide()
        if (window.purchaseUrl) {
          // Add Rewardful referral to the purchase URL if it exists
          if (window.Rewardful && window.Rewardful.referral) {
            const url = new URL(window.purchaseUrl, window.location.origin)
            url.searchParams.set('referral', window.Rewardful.referral)
            window.location.href = url.toString()
          } else {
            window.location.href = window.purchaseUrl
          }
        } else {
          window.location.href = '/'
        }
        break
      case OnBoardingGoal.CreateAccountForGermany:
        modal?.hide()
        window.location.href = "/de-offer"
        break
      case OnBoardingGoal.CreateAccountForGiftCard:
        modal?.hide()
        if (window.giftCardUrl) {
          window.location.href = window.giftCardUrl
        } else {
          window.location.href = '/'
        }
        break
      case OnBoardingGoal.CompleteHike:
        modal?.hide()
        window.location.reload()
        return
      case OnBoardingGoal.TrailMap:
        modal?.hide()
        // Redirect to the map URL stored in sessionStorage
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin')
          window.location.href = redirectUrl
        } else {
          // If no redirect URL, just close the modal
          store.commit('pushPage', OnBoardingPage.Success)
        }
        break
      case OnBoardingGoal.TrailPreview:
        modal?.hide()
        // Trigger the preview modal after login
        if (window.showMapModalForTrail && window.trail_id) {
          window.showMapModalForTrail(window.trail_id)
        }
        break
      default:
        store.commit('pushPage', OnBoardingPage.Success)
    }
  },
  // --- Profile wizard (Name → Country → Username) --------------------------
  // The web equivalent of the mobile apps' post-signup steps. Answers are held
  // in the store and written in a single updateUser call on the last step, so
  // abandoning the wizard half way never leaves a partially-filled profile.

  // Seeds the draft from whatever we already know (Rails record first, then the
  // identity provider) and opens the first step. Countries are fetched in the
  // background so the Country step is ready by the time the user reaches it.
  startProfileSetup(store) {
    const serverUser = store.state.hiikerUser
    const firebaseUser: any = store.state.user
    const { firstName, lastName } = splitFullName(initialFullName(serverUser, firebaseUser?.displayName))
    store.commit('setProfileFirstName', firstName)
    store.commit('setProfileLastName', lastName)
    store.commit('setProfileCountryId', serverUser?.country?.id ?? null)
    // The email signup seeds a placeholder display name from the address so an
    // abandoned wizard isn't nameless. That placeholder must not be offered
    // back as the suggested username, or everyone ends up called
    // "firstname.lastname1987". Only a name the user actually chose is kept.
    const serverName = (serverUser?.displayName || '').trim()
    const isEmailPlaceholder = !!serverName && serverName === nameSeed(null, firebaseUser?.email)
    store.commit('setProfileUsername', isEmailPlaceholder ? '' : serverName)
    store.commit('setError', null)
    // Rails publishes these on the page; absent is fine and means the settings
    // step simply has nothing to offer.
    store.commit('setPreferenceOptions', parsePreferenceOptions((window as any).hiikerPreferenceOptions))
    store.commit('pushPage', OnBoardingPage.ProfilePhoto)
    store.dispatch('fetchCountries')
  },
  async fetchCountries(store) {
    if (store.state.countries.length > 0 || store.state.countriesLoading) return
    store.commit('setCountriesLoading', true)
    store.commit('setCountriesFailed', false)
    try {
      const response = await graphClient.request(GetCountries)
      // The countries table holds states as well as countries and has genuine
      // duplicate rows, so the raw list would show the same name twice.
      const list = dedupeCountries(response?.countries || [])
      store.commit('setCountries', list)
      // A successful-but-empty response is the same dead end for the user as a
      // failed one — nothing to pick, and a Continue that can never enable — so
      // it takes the same recovery path rather than only the throwing case.
      if (list.length === 0) store.commit('setCountriesFailed', true)
    } catch (e) {
      // Flagged rather than silently empty: an empty list is indistinguishable
      // from "no match" in the UI, which would strand the user on a step whose
      // Continue can never enable.
      console.warn('[OnBoarding] Could not load the country list.', e)
      store.commit('setCountries', [])
      store.commit('setCountriesFailed', true)
    } finally {
      store.commit('setCountriesLoading', false)
    }
  },
  // Used by the Country step's retry; clears the cached failure so
  // fetchCountries will actually run again.
  async retryFetchCountries(store) {
    store.commit('setCountriesFailed', false)
    await store.dispatch('fetchCountries')
  },
  submitProfileName(store, parts: { firstName: string, lastName: string }) {
    store.commit('setError', null)
    store.commit('setProfileFirstName', parts.firstName)
    store.commit('setProfileLastName', parts.lastName)
    store.commit('pushPage', OnBoardingPage.ProfileCountry)
  },
  submitProfileCountry(store, countryId: string | number | null) {
    store.commit('setError', null)
    store.commit('setProfileCountryId', countryId)
    // Suggestions are derived from the name the user just gave us, so they can
    // only be computed once we are on the way to the Username step. They are
    // always offered — an email signup arrives with an auto-derived username,
    // and suppressing the chips for those users would leave them no way to see
    // a suggestion at all.
    const firebaseUser: any = store.state.user
    const fullName = composeFullName(store.state.profileFirstName, store.state.profileLastName)
    const suggestions = suggestUsernames(fullName, firebaseUser?.email)
    store.commit('setProfileUsernameSuggestions', suggestions)
    // Prefill only when there is nothing there; never overwrite a name the
    // user (or an earlier step) already chose.
    if (!store.state.profileUsername && suggestions.length > 0) {
      store.commit('setProfileUsername', suggestions[0])
    }
    store.commit('pushPage', OnBoardingPage.ProfileUsername)
  },
  submitProfileUsername(store, username: string) {
    store.commit('setError', null)
    store.commit('setProfileUsername', username)
    store.commit('pushPage', OnBoardingPage.ProfileSettings)
  },
  // One of the bundled HiiKER avatars. Sent as a URL the server fetches, which
  // is the existing remote-avatar path; no upload needed for these.
  selectPresetAvatar(store, preset: { id: string, src: string }) {
    store.commit('setError', null)
    revokeObjectPreview(store.state.profileAvatarPreview)
    store.commit('setProfileAvatar', {
      url: absoluteAvatarUrl(preset.src),
      key: null,
      preview: preset.src,
      presetId: preset.id
    })
  },
  // A photo from the user's device. Only held here — see profileAvatarFile for
  // why it isn't uploaded until the end.
  selectCustomAvatar(store, file: File) {
    store.commit('setError', null)
    if (!file.type || !file.type.startsWith('image/')) {
      store.commit('setError', t('on_boarding.profile_photo_page.not_an_image'))
      return
    }
    if (file.size > MAX_AVATAR_BYTES) {
      store.commit('setError', t('on_boarding.profile_photo_page.photo_too_large'))
      return
    }
    // Release the previous object URL before replacing it; nothing else ever
    // revokes them, so picking several photos would leak each one.
    revokeObjectPreview(store.state.profileAvatarPreview)
    store.commit('setProfileAvatar', {
      url: null, key: null, presetId: null,
      preview: URL.createObjectURL(file),
      file
    })
  },
  // Pushes the held photo to storage at the end of the wizard. Returns the
  // signed id for updateUser, or null when there is nothing to upload.
  async uploadHeldAvatar(store): Promise<string | null> {
    // Already uploaded — a retry after a failed updateUser must reuse the blob
    // rather than pushing a second copy and orphaning the first.
    if (store.state.profileAvatarKey) return store.state.profileAvatarKey
    const file = store.state.profileAvatarFile
    if (!file) return null
    store.commit('setProfileAvatarUploading', true)
    try {
      const checksumBase64 = await md5Base64(file)
      const response = await graphClient.request(GenerateDirectUpload, {
        input: {
          filename: file.name,
          byteSize: file.size,
          contentType: file.type || 'image/jpeg',
          checksumBase64,
          serviceName: (window as any).publicBucketService
        }
      })
      const upload = response?.generateDirectUpload
      if (!upload) throw new Error('Missing upload credentials.')

      const headers = upload.headers ? JSON.parse(upload.headers) : {}
      const put = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': file.type || 'image/jpeg' },
        body: file
      })
      if (!put.ok) throw new Error(`Upload failed with status ${put.status}.`)
      // Record the key and release the file, so the upload happens exactly once.
      store.commit('setProfileAvatar', {
        url: null,
        key: upload.signedId,
        preview: store.state.profileAvatarPreview,
        presetId: null,
        file: null
      })
      return upload.signedId
    } finally {
      store.commit('setProfileAvatarUploading', false)
    }
  },
  submitProfilePhoto(store) {
    store.commit('setError', null)
    store.commit('pushPage', OnBoardingPage.ProfileName)
  },
  submitProfileSettings(store) {
    store.commit('setError', null)
    store.commit('pushPage', OnBoardingPage.ProfileSource)
  },
  // Last step: persist everything the wizard collected in one call, then hand
  // back to the normal post-login routing so the user ends up where they were
  // always going. Writing once at the end means abandoning the wizard part way
  // never leaves a half-filled profile.
  async submitProfileSource(store, attributionTag?: string | null) {
    store.commit('setError', null)
    // Only a value from the offered list. `attribution_tag` is free text with
    // nothing validating it server-side, so anything else would quietly become
    // a new "source" in the reporting.
    //
    // An explicit `null` means the user deselected their answer and must clear
    // it; only an omitted argument falls back to the stored draft. Treating the
    // two the same made a deselection impossible to save.
    const stored = isAttributionSource(store.state.profileAttributionTag)
      ? store.state.profileAttributionTag
      : null
    const source = attributionTag === undefined
      ? stored
      : (isAttributionSource(attributionTag) ? attributionTag as string : null)
    store.commit('setProfileAttributionTag', source)
    store.commit('setLoading', true)
    try {
      const user: any = { displayName: store.state.profileUsername }
      const fullName = composeFullName(store.state.profileFirstName, store.state.profileLastName)
      if (fullName) user.title = fullName
      if (store.state.profileCountryId != null) user.countryId = Number(store.state.profileCountryId)
      // Only one of these is ever set — a preset is a URL the server fetches,
      // an uploaded photo a direct-upload signed id.
      let uploadedKey: string | null = null
      try {
        uploadedKey = await store.dispatch('uploadHeldAvatar')
      } catch (uploadError) {
        // "Upload failed with status 403" is not something to show a person.
        console.error('[OnBoarding] Avatar upload failed:', uploadError)
        store.commit('setError', t('on_boarding.on_boarding_module.could_not_upload_photo'))
        store.commit('setLoading', false)
        return
      }
      if (uploadedKey) user.avatarKey = uploadedKey
      else if (store.state.profileAvatarUrl) user.avatar = store.state.profileAvatarUrl

      const localeCodes = parseLocales((window as any).hiikerLocales).map((locale: any) => locale.code)
      Object.assign(user, settingsPayload({
        measurementPreference: store.state.profileMeasurement,
        gridReference: store.state.profileGridReference,
        hikeTime: store.state.profileHikeTime,
        locale: store.state.profileLocale
      }, store.state.preferenceOptions, localeCodes))

      // Optional — an unanswered question simply isn't sent.
      if (source) user.attributionTag = source

      await graphClient.request(UpdateUser, { user })
      emitter.emit('user:updated')
    } catch (e: any) {
      // Surface the failure and stay put: the user can retry rather than lose
      // everything they just entered.
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_save_profile'))
      store.commit('setLoading', false)
      return
    }
    store.commit('setLoading', false)
    store.commit('setProfileSetupCompleted', true)
    await store.dispatch('finishSignupRouting')
  },
  // The single exit from a signup: show the "Check your inbox" nudge when the
  // account was created with an email and password (verifyEmailAddress is only
  // set there), otherwise route straight on. Kept in one place so every path
  // that completes a signup — the profile steps, ConfirmDetails — behaves the
  // same. The screen's own Continue re-enters setNextPostLoginPage, which falls
  // through to routeAfterLogin because the signup is marked finished by then.
  async finishSignupRouting(store) {
    if (store.state.verifyEmailAddress) {
      store.commit('pushPage', OnBoardingPage.VerifyEmailSent)
      return
    }
    await store.dispatch('routeAfterLogin')
  },
  downloadGPX(store) {
    store.commit('pushPage', OnBoardingPage.Success)
  },
  downloadKML(store) {
    store.commit('pushPage', OnBoardingPage.Success)
  },
  downloadGeoJSON(store) {
    store.commit('pushPage', OnBoardingPage.Success)
  },
  async getUserHikeLists(store) {
    try {
      const response = await graphClient.request(GetUserHikeLists)
      store.commit('setUsersHikeLists', response.userHikeLists)
    } catch (e: any) {
      console.error("Error fetching user hike lists:", e);
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_load_hike_lists'))
    }
  },
  async createHikeList(store, name: string) {
    try {
      const response = await graphClient.request(CreateHikeList, { 
        input: {
          name, 
          hikeIds: [store.state.hikeId]
        }
      })
      if (response.createHikeList.errors.length > 0) {
        throw new Error(response.createHikeList.errors[0].errors[0])
      }
      await store.dispatch('getUserHikeLists')
    } catch (e: any) {
      console.error("Error creating hike list:", e);
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_create_list'))
    }
  },
  async toggleHikeInList(store, hikeList: any) {
    try {
      const hikeId = store.state.hikeId
      const isInList = hikeList.hikes?.some((hike: any) => hike.id === hikeId)
      
      if (isInList) {
        const response = await graphClient.request(RemoveHikeFromList, { 
          input: {
            hikeListId: hikeList.id, 
            hikeId: hikeId
          }
        })
        if (response.removeHikeFromList.errors.length > 0) {
          throw new Error(response.removeHikeFromList.errors[0].errors[0])
        }
      } else {
        const response = await graphClient.request(AddHikeToList, { 
          input: {
            hikeListId: hikeList.id, 
            hikeId: hikeId
          }
        })
        if (response.addHikeToList.errors.length > 0) {
          throw new Error(response.addHikeToList.errors[0].errors[0])
        }
      }
      await store.dispatch('getUserHikeLists')
    } catch (e: any) {
      console.error("Error toggling hike in list:", e);
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_update_list'))
    }
  },
  async signUpWithEmail(store, details: { email: string, password: string, fullName: string, displayName?: string }) {
    store.commit('setError', null)
    store.commit('setLoading', true)
    store.commit('setAuthInProgress', true)
    try {
      const userResponse = await createUserWithEmailAndPassword(auth, details.email, details.password)
      if (userResponse.user) {
        store.commit('setUser', userResponse.user)
        await store.dispatch('sendAccessToken')
        // The name is no longer collected on this form — the profile steps ask
        // for it straight after, exactly as the mobile apps do (email →
        // password → first & last name). Only the fields this screen still owns
        // are written here.
        // A placeholder display name from the email local-part. The username
        // step overwrites it moments later, but if the user abandons the wizard
        // the account still has something to show rather than a blank the
        // nightly task later replaces with a random "SummitRidge42".
        const fallbackDisplayName = details.displayName?.trim() || nameSeed(details.fullName, details.email)
        await graphClient.request(UpdateUser, {
          user: {
            ...(details.fullName ? { title: details.fullName } : {}),
            ...(fallbackDisplayName ? { displayName: fallbackDisplayName } : {}),
            deOfferUnlock: window.de_offer_unlock
          }
        })
        window.user = userResponse.user
        window.isNewUser = true
        emitter.emit('user:updated')
        // A verification email is sent server-side when the account is created, but
        // it must not gate the next steps — mobile doesn't wait for it either. The
        // "Check your inbox" screen is shown at the END of the profile steps instead
        // (see submitProfileUsername), so the user keeps moving.
        store.commit('setVerifyEmailAddress', details.email)
        // confirmDetailsFallback: this form no longer collects a name, so when the
        // profile steps don't apply (an interruption flow) ConfirmDetails still has
        // to ask for one — otherwise the account would end up nameless.
        await store.dispatch('setNextPostLoginPage', { confirmDetailsFallback: true })
      }
    } catch (e: any) {
      store.commit('setError', friendlyAuthError(e, t('on_boarding.on_boarding_module.could_not_create_account')))
    } finally {
      store.commit('setLoading', false)
      store.commit('setAuthInProgress', false)
    }
  },
  // Re-send the account confirmation email (server-side Rails/Airship send). Used by the post-signup
  // "Check your inbox" screen's resend link. Mirrors POST /user/resend_confirmation.
  async resendVerificationEmail(store) {
    store.commit('setError', null)
    store.commit('setNotice', null)
    try {
      await axios_client.post('/user/resend_confirmation')
      store.commit('setNotice', t('on_boarding.on_boarding_module.resend_sent'))
    } catch (e: any) {
      store.commit('setError', e?.response?.data?.message || t('on_boarding.on_boarding_module.could_not_resend_email'))
    }
  },
  async signInWith(store, provider: SignInProvider) {
    let authProvider;
    switch (provider) {
      case SignInProvider.Google:
        authProvider = new GoogleAuthProvider();
        break
      case SignInProvider.Facebook:
        authProvider = new FacebookAuthProvider();
        break
      case SignInProvider.Apple:
        authProvider = new OAuthProvider('apple.com');
        authProvider.addScope('email');
        authProvider.addScope('name');
        break
      case SignInProvider.Email:
        return store.commit('pushPage', OnBoardingPage.EmailSignUp)
      case SignInProvider.MagicLink:
        return store.commit('pushPage', OnBoardingPage.MagicLink)
    }
    store.commit('setError', null)
    store.commit('setLoading', true)
    store.commit('setAuthInProgress', true)
    try {
      const signInResponse = await signInWithPopup(auth, authProvider)
      const user = signInResponse.user
      const isNewUser = signInResponse._tokenResponse?.isNewUser

      if (user) {
        store.commit('setUser', user)
        await store.dispatch('sendAccessToken')

        // Set window.isNewUser if it's a new user
        if (isNewUser) {
          window.isNewUser = true
        }

        await graphClient.request(GetUser, { firebaseID: user.uid })
        window.user = user
        emitter.emit('user:updated')

        // SSO providers (Google, Apple, Facebook) usually return a populated
        // displayName. When they don't, we still need to ask for a name —
        // setNextPostLoginPage prefers the profile wizard and falls back to the
        // ConfirmDetails screen when the wizard doesn't apply.
        await store.dispatch('setNextPostLoginPage', {
          confirmDetailsFallback: !(user.displayName && user.displayName.trim().length > 0)
        })
      }
    } catch (e: any) {
      store.commit('setError', friendlyAuthError(e, t('on_boarding.on_boarding_module.sign_in_failed')))
    } finally {
      store.commit('setLoading', false)
      store.commit('setAuthInProgress', false)
    }
  },
  async requestMagicLink(store, email: string) {
    store.commit('setError', null)
    if (!email || email.indexOf('@') === -1) {
      store.commit('setError', t('on_boarding.on_boarding_module.enter_valid_email'))
      return
    }
    store.commit('setLoading', true)
    try {
      // Rails emails a /user/onboarding-login link, which the apps open and sign in from.
      await axios_client.post('/api/v1/magic-login', { email })
      store.commit('setMagicLinkEmail', email)
    } catch (e: any) {
      const status = e?.response?.status
      store.commit('setError', status === 429 ? t('utils.auth_errors.too_many_requests')
        : status === 422 ? t('utils.auth_errors.invalid_email')
          : t('on_boarding.on_boarding_module.could_not_send_sign_in_link'))
    } finally {
      store.commit('setLoading', false)
    }
  },
  // Completes the email-link sign-in when the user returns via the link in
  // their inbox. Pass an explicit email when the user re-entered it because
  // localStorage didn't have it (different browser / cleared storage).
  async completeMagicLinkSignIn(store, providedEmail?: string) {
    const url = window.location.href
    if (!isSignInWithEmailLink(auth, url)) {
      return
    }
    store.commit('setError', null)
    store.commit('setLoading', true)
    store.commit('setAuthInProgress', true)
    try {
      const storedEmail = window.localStorage.getItem(MAGIC_LINK_EMAIL_STORAGE_KEY)
      // The onboarding auto-login link (UsersController#onboarding_login) carries
      // the customer's address on the continueUrl as `prefill_email`, since a
      // first-time customer clicking from their inbox has nothing in localStorage.
      // When present it is authoritative — the backend minted this link for
      // exactly that address — so it must win over a localStorage email left
      // behind by an earlier magic-link request, which Firebase would reject
      // as a mismatch (auth/invalid-email).
      const urlEmail = new URLSearchParams(window.location.search).get('prefill_email')
      const email = (providedEmail && providedEmail.trim()) || urlEmail || storedEmail
      if (!email) {
        store.commit('setMagicLinkNeedsEmail', true)
        return
      }
      const credential = await signInWithEmailLink(auth, email, url)
      window.localStorage.removeItem(MAGIC_LINK_EMAIL_STORAGE_KEY)
      store.commit('setMagicLinkEmail', null)
      store.commit('setMagicLinkNeedsEmail', false)
      if (credential.user) {
        store.commit('setUser', credential.user)
        // Mirror the loginWithEmail / signInWith flow: send the access token
        // to /firebase/auth to create the Rails session, then route the user.
        await store.dispatch('sendAccessToken')
        const isNewUser = (credential as any)._tokenResponse?.isNewUser
        if (isNewUser) {
          window.isNewUser = true
        }
        await graphClient.request(GetUser, { firebaseID: credential.user.uid })
        window.user = credential.user
        emitter.emit('user:updated')
        // Strip the magic-link query params so a refresh doesn't try to
        // consume the (now-spent) one-time code and surface a stale error.
        window.history.replaceState({}, document.title, window.location.pathname)
        await store.dispatch('setNextPostLoginPage', {
          confirmDetailsFallback: !(credential.user.displayName && credential.user.displayName.trim().length > 0)
        })
      }
    } catch (e: any) {
      // auth/invalid-action-code covers expired and already-used links.
      store.commit('setError', friendlyAuthError(e, t('on_boarding.on_boarding_module.sign_in_link_invalid')))
    } finally {
      store.commit('setLoading', false)
      store.commit('setAuthInProgress', false)
    }
  },
  async loginWithEmail(store, details: { email: string, password: string }) {
    store.commit('setError', null)
    store.commit('setLoading', true)
    store.commit('setAuthInProgress', true)
    try {
      const userResponse = await signInWithEmailAndPassword(auth, details.email, details.password)
      if (userResponse.user) {
        store.commit('setUser', userResponse.user)
        await store.dispatch('sendAccessToken')
        window.user = userResponse.user
        emitter.emit('user:updated')
        // Returning user — go straight to their post-login destination.
        await store.dispatch('setNextPostLoginPage')
      } else {
        store.commit('setError', t('on_boarding.on_boarding_module.could_not_log_in'))
      }
    } catch (e: any) {
      store.commit('setError', friendlyAuthError(e, t('on_boarding.on_boarding_module.could_not_log_in')))
    } finally {
      store.commit('setLoading', false)
      store.commit('setAuthInProgress', false)
    }
  },
  async sendAccessToken(store) {
    try {
      if (store.state.user) {
        const token = await store.state.user.getIdToken()
        const redirectUrl = `/firebase/auth.json?accessToken=${token}`;
        graphClient.setHeader('Authorization', token);
        await axios_client.get(redirectUrl)
      } else {
        throw Error("No user found")
      }
    } catch (e: any) {
      store.commit('setError', friendlyAuthError(e, t('on_boarding.on_boarding_module.could_not_sign_in')))
    }
  },
  async updateUser(store, userDetails: { fullName: string, displayName: string }) {
    const { fullName, displayName } = userDetails;

    // Retrieve fbclid from the current URL
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    // Construct the payload for the GraphQL request
    const userPayload = {
      title: fullName,
      deOfferUnlock: window.de_offer_unlock,
      displayName,
      ...(fbclid && {
        fbclid,
        fbcltime: new Date(performance.timeOrigin).toISOString() // Use timeOrigin instead of navigationStart
      }),
    };

    // Send the mutation request
    await graphClient.request(UpdateUser, { user: userPayload });

    // This screen collects the same name and username the profile steps would,
    // so reaching the end of it completes the signup. Marking that stops the
    // gate firing on the way out and asking for the same details again: the
    // `hiikerUser` in the store is still the pre-update copy, so the gate would
    // otherwise see a blank profile and launch the three-step wizard.
    store.commit('setProfileSetupCompleted', true);
    // finishSignupRouting rather than setNextPostLoginPage, so an email signup
    // that landed here still gets its "Check your inbox" screen.
    await store.dispatch('finishSignupRouting');
  },
  async updateUserForFb(store) {

    // Retrieve fbclid from the current URL
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (!fbclid || !window.user) {
      return
    }

    // Construct the payload for the GraphQL request
    const userPayload = {
      ...(fbclid && {
        fbclid,
        fbcltime: new Date(performance.timeOrigin).toISOString() // Use timeOrigin instead of navigationStart
      }),
    };

    // Send the mutation request
    await graphClient.request(UpdateUser, { user: userPayload });
  },
  async getHiikerUser(store) {
    if (!store.state.user) {
      throw Error("No user found")
    }

    const firebaseUser: any = store.state.user
    const firebaseId = firebaseUser?.uid
    if (!firebaseId) {
      console.warn('[OnBoarding] Firebase user missing uid; skipping Hiiker user lookup.')
      store.commit('setHiikerUser', null)
      return
    }

    try {
      const token = typeof firebaseUser.getIdToken === 'function' ? await firebaseUser.getIdToken() : null
      const headers = token ? { Authorization: token } : undefined
      const hiikerUser = await graphClient.request(GetUser, { firebaseID: firebaseId }, headers)
      if (hiikerUser?.user) {
        store.commit('setHiikerUser', hiikerUser.user)
      }
    } catch (e) {
      if (import.meta.env?.DEV) {
        console.warn('[OnBoarding] Failed to fetch Hiiker user; continuing without backend profile.', e)
      } else {
        console.warn('[OnBoarding] Failed to fetch Hiiker user; continuing without backend profile.')
      }
      store.commit('setHiikerUser', null)
    }
  },
  async copyCuratedTrailToPlannedTrails(store) {
    try {
      await graphClient.request(CopyCuratedTrailToPtv2PlannedTrail, { trail: { id: store.state.trailId } })
    } catch (e: any) {
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_save_trail'))
    }
  },
  async getClientSecret(store) {
    if (store.state.offering == null) {
      throw Error("No price found")
    }

    let response = await axios_client.post('/subscriptions.json', { price_id: store.state.offering.price.id, landing_page_slug: window.landingPageSlug })
    await store.commit('setClientSecret', response.data.client_secret)
  },
  async getCheckoutPage(store) {
    if (store.state.offering == null) {
      throw Error("No offering found")
    }
    // @ts-ignore
    window.location = `/purchases/start_checkout_session?price_id=${store.state.offering.price.id}&success_url=${window.location.origin + window.location.pathname}`
  },
  async retrieveFreeTrialPaymentMethodClientSecret(store) {
    try {
      const response = await axios_client.post(store.state.paymentMethodClientSecretSubscriptionsUrl)
      store.commit('setFreeTrialPaymentMethodClientSecret', response.data.client_secret)
    } catch (e: any) {
      store.commit('setError', e?.message || t('on_boarding.on_boarding_module.could_not_start_free_trial'))
    }
  },
  async resetPassword(store, email: string) {
    store.commit('setError', null)
    store.commit('setNotice', null)
    try {
      await sendPasswordResetEmail(auth, email)
      // Always show the same confirmation regardless of whether the address
      // is registered — leaking existence on this endpoint is a known reset
      // flow anti-pattern.
      store.commit('setNotice', t('on_boarding.on_boarding_module.reset_link_sent'))
    } catch (error: any) {
      // For most reset errors we mirror the success message to avoid leaking
      // account existence (e.g. auth/user-not-found). Only surface real
      // errors that the user can act on (rate limiting, invalid format,
      // network).
      const code = (error && typeof error.code === 'string') ? error.code : ''
      const leakySafeCodes = ['auth/user-not-found', 'auth/invalid-email']
      if (leakySafeCodes.includes(code)) {
        store.commit('setNotice', t('on_boarding.on_boarding_module.reset_link_sent'))
      } else {
        store.commit('setError', friendlyAuthError(error, t('on_boarding.on_boarding_module.could_not_send_reset_email')))
      }
    }
  }
}

const mutations: MutationTree<OnBoardingState> = {
  setAuthMethods(state, authMethods: AuthMethods[]) {
    state.authMethods = authMethods
  },
  setError(state, message: string | null) {
    state.errorMessage = message
  },
  setNotice(state, message: string | null) {
    state.noticeMessage = message
  },
  setVerifyEmailAddress(state, email: string | null) {
    state.verifyEmailAddress = email
  },
  setLoading(state, loading: boolean) {
    state.isLoading = loading
  },
  setStandaloneMode(state, standalone: boolean) {
    state.standaloneMode = standalone
  },
  setMagicLinkEmail(state, email: string | null) {
    state.magicLinkEmail = email
  },
  setMagicLinkNeedsEmail(state, needs: boolean) {
    state.magicLinkNeedsEmail = needs
  },
  setFreeTrialPaymentMethodClientSecret(state, clientSecret: string) {
    state.freeTrialPaymentMethodClientSecret = clientSecret
  },
  setClientSecret(store, secret: string) {
    store.clientSecret = secret
  },
  setTrail(state, trail) {
    state.trail = trail
  },
  setUsersHikeLists(state, hikeLists: any[]) {
    state.usersHikeLists = hikeLists
  },
  setAuthInProgress(state, inProgress) {
    state.authInProgress = inProgress
  },
  setProfileAvatar(state, payload: { url?: string | null, key?: string | null, preview?: string | null, presetId?: string | null, file?: File | null }) {
    state.profileAvatarUrl = payload.url ?? null
    state.profileAvatarKey = payload.key ?? null
    state.profileAvatarPreview = payload.preview ?? null
    state.profileAvatarPresetId = payload.presetId ?? null
    state.profileAvatarFile = payload.file ?? null
  },
  setProfileAvatarUploading(state, uploading) {
    state.profileAvatarUploading = uploading
  },
  setPreferenceOptions(state, options) {
    state.preferenceOptions = options
  },
  setProfileMeasurement(state, value) {
    state.profileMeasurement = value
  },
  setProfileGridReference(state, value) {
    state.profileGridReference = value
  },
  setProfileHikeTime(state, value) {
    state.profileHikeTime = value
  },
  setProfileLocale(state, value) {
    state.profileLocale = value
  },
  setProfileAttributionTag(state, value) {
    state.profileAttributionTag = value
  },
  setProfileSetupCompleted(state, completed) {
    state.profileSetupCompleted = completed
  },
  setCountries(state, countries) {
    state.countries = countries
  },
  setCountriesLoading(state, loading) {
    state.countriesLoading = loading
  },
  setCountriesFailed(state, failed) {
    state.countriesFailed = failed
  },
  setProfileFirstName(state, firstName) {
    state.profileFirstName = firstName
  },
  setProfileLastName(state, lastName) {
    state.profileLastName = lastName
  },
  setProfileCountryId(state, countryId) {
    state.profileCountryId = countryId
  },
  setProfileUsername(state, username) {
    state.profileUsername = username
  },
  setProfileUsernameSuggestions(state, suggestions) {
    state.profileUsernameSuggestions = suggestions
  },
  setHiikerUser(state, hiikerUser) {
    state.hiikerUser = hiikerUser
  },
  setUser(state, user: firebase.User) {
    state.user = user
  },
  setSlugData(state) {
    state.slug = window.slug;
    state.regionSlug = window.region_slug;
    state.countrySlug = window.country_slug;
    state.hikeId = window.hike_id || window.id?.toString() || ''
    state.trailId = window.id
  },
  setPageStack(state, pageStack: OnBoardingPage[]) {
    state.pageStack = pageStack
  },
  pushPage(state, page: OnBoardingPage) {
    state.pageStack.push(page)
  },
  popPage(state) {
    state.pageStack.pop()
  },
  setOnBoardingGoal(state, goal: OnBoardingGoal) {
    state.onBoardingGoal = goal
  },
  setOffering(state, offering: Offering) {
    state.offering = offering
  },
  setContextFromTrail(state, context: OnBoardingTrailContext = {}) {
    const { hikeId, trailId, slug, regionSlug, countrySlug } = context

    if (hikeId != null) {
      state.hikeId = hikeId.toString()
    }

    if (trailId != null) {
      state.trailId = Number(trailId)
    }

    if (slug) {
      state.slug = slug
    }

    if (regionSlug) {
      state.regionSlug = regionSlug
    }

    if (countrySlug) {
      state.countrySlug = countrySlug
    }
  }
}

const getters: GetterTree<OnBoardingState, RootState> = {
  // A deliberate signup (navbar Sign up, a purchase, a gift card, or the
  // standalone /login page) runs the profile steps afterwards; an interruption
  // — someone who clicked "like" or "add to list" — does not. Only the former
  // has progress worth showing on the email form.
  isDeliberateSignup(state): boolean {
    return !!state.standaloneMode || isDeliberateSignupGoal(state.onBoardingGoal as any)
  },
  lastPage(store) {
    return store.pageStack[store.pageStack.length - 1]
  },
  trailUrl(store) {
    return `/trails/${store.countrySlug}/${store.regionSlug}/${store.slug}`
  },
}

const state = (): OnBoardingState => {
  return new OnBoardingState();
}


const onBoardingModule: Module<OnBoardingState, RootState> = {
  namespaced: true,
  state, // Use the state factory function
  actions,
  mutations,
  getters
};

export default onBoardingModule;
